import { ClinicalLicensure, Patient } from 'src/app/models/patient.model';
import { GuideRole } from 'src/app/models/care-call-guide.model';
import { User } from 'src/app/models/user.model';

/**
 * Resolves the care-call role from the user's CLINICAL LICENSURE on this patient's care team.
 *
 * ## Why not a role flag
 *
 * This replaced `NewAuthService.careRole`, which read `roles.isManagerNurse`. That flag does not
 * mean "nurse care manager" — the roles dialog labels it **"Manager Nurse"**, and every other
 * consumer uses it for supervision: who may view another nurse's calls, who is a lead. In dev only
 * 5 of 228 clinical users carry it, while `Registered Nurse` appears 212 times across patient care
 * teams. So the overwhelming majority of nurses resolved to `'ma'` — they saw escalation prompts
 * telling them to escalate to a nurse, and their generated note printed `(MA)` under their name.
 *
 * The licensure is the right input, and the rest of the platform already uses it: the alert engine
 * routes `owner: 'rn'` through `SHORT_CODE_TO_LICENSURE` against `patient.providers[].role`, and
 * `resolveNcmAssignee` in the guide itself resolves the NCM as RN then NP off the same array.
 * "RN" and "NCM" are the same person in this platform's vocabulary — there is no separate NCM role
 * anywhere in any repo.
 *
 * ## Why it takes a patient
 *
 * Licensure is a property of the provider-to-patient RELATIONSHIP, not of the user: `User` carries
 * no licensure field, only `patient.providers[].role` does. So the question cannot be answered
 * without a patient, which is why this is a function here rather than a getter on the auth service
 * (which has no patient, and cannot take one without a circular dependency on PatientService).
 *
 * ## Why anything-but-MA is a nurse
 *
 * The platform routes to exactly two queues, `owner: 'ma' | 'rn'`, so the role is a binary and the
 * MA is the side that has to be named. Everything else on a care team — RN, NP, LVN, Care
 * Coordinator — resolves to `'ncm'`.
 *
 * A user who is NOT on the patient's care team also resolves to `'ncm'`, and that is deliberate:
 * `'ncm'` is the side that PRODUCES NOTHING. `suppressEscalations` is `role === 'ncm'`, so an
 * admin, a covering nurse or anyone browsing a chart they do not manage raises no alert and no
 * task. Guessing a role from the user's own flags would have handed them the escalating side.
 */
export const resolveCareCallRole = (
  user: User | null | undefined,
  patient: Patient | null | undefined
): GuideRole => {
  const uid = user?.user_id;
  if (!uid) {
    return 'ncm';
  }
  const onCareTeam = patient?.providers?.find((provider) => provider?.uid === uid);
  // `role` is written from the untyped `welby_configuration/provider_roles` list, which already holds
  // an entry with a leading space. Normalising keeps a stray space or case change in that document
  // from silently turning every MA into 'ncm' and switching the escalation path off.
  const licensure = String(onCareTeam?.role ?? '').trim().toLowerCase();
  return licensure === ClinicalLicensure.MEDICAL_ASSISTANT.toLowerCase() ? 'ma' : 'ncm';
};
